import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

export interface SuggestItemResponse {
  key: string;
  text: string;
}

interface SpatialReferenceLike {
  wkid: number;
}

export interface AddressItemResponse {
  address: string;
  score: number;
  location: { x: number; y: number; spatialReference: SpatialReferenceLike };
  extent: {
    xmin: number;
    ymin: number;
    xmax: number;
    ymax: number;
    spatialReference: SpatialReferenceLike;
  };
}

export interface AddressCandidateSearchOptions {
  text?: string;
  key?: string;
  maxSuggestions?: number;
  outWkid?: number;
}

export interface AddressCandidateSearchBestOptions
  extends Omit<AddressCandidateSearchOptions, 'maxSuggestions'> {}

const sourceCountry = 'PT';

@Injectable()
export class AddressService {
  constructor(private http: HttpClient) {}

  findSuggests(
    text: string,
    maxSuggestions?: number
  ): Observable<SuggestItemResponse[]> {
    return this.http
      .get<{ suggestions: any[] }>(`~addressService/suggest`, {
        params: {
          f: 'json',
          text,
          maxSuggestions: maxSuggestions ?? 6,
          sourceCountry,
        },
      })
      .pipe(
        map((res) => {
          return (
            res?.suggestions?.map<SuggestItemResponse>((item) => {
              return {
                key: item.magicKey,
                text: item.text,
              };
            }) ?? []
          );
        })
      );
  }

  findBestAddressCandidate(
    options: AddressCandidateSearchBestOptions
  ): Observable<AddressItemResponse> {
    options = Object.assign({}, options, { maxSuggestions: 1 });
    return this.findAddressCandidates(options).pipe(
      map((res) => {
        return res.at(0);
      })
    );
  }

  findAddressCandidates({
    text,
    key,
    maxSuggestions,
    outWkid,
  }: AddressCandidateSearchOptions): Observable<AddressItemResponse[]> {
    let params = new HttpParams({
      fromObject: {
        f: 'json',
        outSR: JSON.stringify({ wkid: outWkid ?? 4326 }),
        sourceCountry,
      },
    });

    if (key) {
      params = params.set('magicKey', key);
    }
    if (text) {
      params = params.set('SingleLine', text);
    }
    if (maxSuggestions) {
      params = params.set('maxSuggestions', maxSuggestions ?? 6);
    }

    return this.http
      .get<{ candidates: any[]; spatialReference: SpatialReferenceLike }>(
        `~addressService/findAddressCandidates`,
        {
          params,
        }
      )
      .pipe(
        map((res) => {
          const spatialReference = res?.spatialReference;

          return (
            res?.candidates?.map<AddressItemResponse>(
              ({ address, score, location, extent }) => {
                location.spatialReference = spatialReference;
                extent.spatialReference = spatialReference;

                return {
                  address,
                  score,
                  location,
                  extent,
                };
              }
            ) ?? []
          );
        })
      );
  }
}
